import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import MenuList from './ContextMenuList'
import type { Position } from './types'
import useFocusWithArrows from './useFocusWithArrows'
import useDetectTouchscreen from './useDetectTouchScreen'
import useActionOnEscape from './useActionOnEscape'
import Icon from '../Icon/Icon'
import { MapButton } from '../SidebarMap/styles'
import { Variant } from '../Button/Button'

const ContextMenuButtonStyle = styled(MapButton)`
  cursor: pointer;
  display: flex;
  background-color: var(--light);
  align-items: center;
  padding: 0 0.5rem;
  justify-content: center;
  z-index: 1;
  border: 2px solid var(--blue);
  height: 44px;
  min-width: 44px;
  gap: 0.5rem;

  svg {
    margin-right: 0;
  }
`

const ContextMenuWrapperStyle = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  position: relative;
`

function ownerDocument(node?: HTMLElement | null) {
  return node?.ownerDocument ?? window.document
}

export interface Props {
  position?: Position
  label?: string
  icon?: ReactNode
  arrowIcon?: ReactNode
  selectElementForTouchScreen?: ReactNode
  open?: boolean
  onToggle?: (open: boolean) => void
}

function ContextMenu({
  className,
  onToggle,
  open: openProp = false,
  label,
  children,
  arrowIcon,
  icon,
  position,
  selectElementForTouchScreen,
  ...otherProps
}: Props & ButtonHTMLAttributes<HTMLButtonElement>) {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(openProp)
  const isTouchScreen = useDetectTouchscreen()

  // Set state with 'open' prop if it's set
  useEffect(() => {
    if (openProp !== open) {
      setOpen(openProp)
    }
  }, [openProp])

  const handleToggle = () => {
    if (onToggle) {
      onToggle(!open)
    } else {
      setOpen(!open)
    }
  }

  const onClose = () => {
    // To prevent the component to close before setting the focus on the children when tabbing or using arrow keys
    setTimeout(() => {
      const element = ref.current
      if (element) {
        const currentFocus = ownerDocument(element).activeElement
        if (!element.contains(currentFocus)) {
          setOpen(false)
        }
      }
    })
  }
  const { keyDown: focusWithArrowKeys } = useFocusWithArrows(ref, true)
  const { onKeyDown: closeOnEscape } = useActionOnEscape(() => {
    setOpen(false)
  })

  return (
    <ContextMenuWrapperStyle
      ref={ref}
      onKeyDown={(e) => {
        focusWithArrowKeys(e)
        closeOnEscape(e)
      }}
      onBlur={onClose}
      className={className}
    >
      <ContextMenuButtonStyle
        size={icon && arrowIcon ? undefined : 44}
        variant={Variant.PrimaryInverted}
        data-testid="toggle"
        onClick={handleToggle}
        disabled={!!(selectElementForTouchScreen && isTouchScreen)}
        {...otherProps}
      >
        {icon && <Icon>{icon}</Icon>}
        {label && <span>{label}</span>}
        {arrowIcon && <Icon rotate={open ? 0 : 180}>{arrowIcon}</Icon>}
      </ContextMenuButtonStyle>
      {((selectElementForTouchScreen && !isTouchScreen) || !selectElementForTouchScreen) && (
        <MenuList position={position} open={open} onClose={onClose}>
          {children}
        </MenuList>
      )}
    </ContextMenuWrapperStyle>
  )
}

export default ContextMenu
